import { StylesConfig } from 'react-select';
import { Theme as ThemeType } from '../../theme';
import { roundValues } from './index';
import { GroupBase } from 'react-select/dist/declarations/src/types';
import { formVariants } from '@/components/Form';

const generateCustomReactSelectStyles = <
  Option = any,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  round: roundValues,
  theme: ThemeType,
  styles?: StylesConfig<Option, IsMulti, Group>
): StylesConfig<Option, IsMulti, Group> => {
  // Basic Text Styling
  const styleTextBody2 = {
    fontFamily: 'inherit',
    fontWeight: theme.fontWeights.body,
    fontSize: theme.fontSizes[1], // fontSize: [1], // 14
    lineHeight: theme.lineHeights.interactive, // 1.142857143
    color: theme.colors.onField,
  };

  /* rounded tests */
  const getRoundValues = () => {
    if (round === 'rounded-left') {
      return {
        borderRadius: 'none',
        borderTopLeftRadius: theme.radii.default,
        borderBottomLeftRadius: theme.radii.default,
      };
    }

    if (round === 'rounded-right') {
      return {
        borderRadius: 'none',
        borderTopRightRadius: theme.radii.default,
        borderBottomRightRadius: theme.radii.default,
      };
    }

    if (round === 'square') {
      return {
        borderRadius: 'none',
      };
    }

    if (round === 'combo') {
      return {
        borderRadius: 'none',
        borderTopLeftRadius: theme.radii.circle,
        borderBottomLeftRadius: theme.radii.circle,
      };
    }
    // round as the default
    else {
      return {
        borderRadius: theme.radii.circle,
      };
    }
  };
  const stylesRound = getRoundValues();

  // Build the custom styles
  // I have no idea what this typing means 🤷‍♀️
  return {
    ...styles,

    // Most Parent Wrapper
    // container: (provided, props) => ({
    //   ...provided,
    //   // width: 'fit-content',
    //   // minWidth: '172px',
    // }),
    // Wraps ValueContainer + IndicatorContainer
    control: (provided, props) => ({
      ...(styles?.control?.(provided, props) ?? provided),
      ...provided,
      ...styleTextBody2,
      ...stylesRound,
      /* override defaults */
      // minHeight: 0,
      /* map current values */
      minHeight: formVariants.field.height,
      alignItems: 'center',
      background: theme.colors.field,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: props.isFocused
        ? theme.colors.primary
        : theme.colors.fieldBorder,
      boxShadow: props.isFocused ? `0 0 0 1px ${theme.colors.primary}` : '',
      '&:hover': {
        borderColor: props.isFocused
          ? theme.colors.primary
          : theme.colors.fieldBorderHover,
      },
    }),

    // Contains one of the following: placeholder || singleValue
    valueContainer: (provided, props) => ({
      ...(styles?.valueContainer?.(provided, props) ?? provided),
      padding: 0,
      paddingLeft: theme.sizes.scale[3],
      paddingRight: 0,
    }),
    // these inherit their parents text styling
    placeholder: (provided, props) => ({
      ...(styles?.placeholder?.(provided, props) ?? provided),
      whiteSpace: 'nowrap',
      // textOverflow: 'ellipsis',
      overflow: 'hidden',
      // ...styleTextBody2,
      opacity: 0.7,
      marginLeft: 0,
    }),
    singleValue: (provided, props) => ({
      ...(styles?.singleValue?.(provided, props) ?? provided),
      // ...styleTextBody2,
      marginLeft: 0,
    }),

    // box containing indicator and separator
    indicatorsContainer: (provided, props) => ({
      ...(styles?.indicatorsContainer?.(provided, props) ?? provided),
      justifyContent: 'center',
      alignItems: 'center',
      // avoid indicator padding fighting
      // width: 32,
      height: '26px',
      alignSelf: 'center',
      // flexGrow: 1,
    }),

    clearIndicator: (provided, props) => ({
      ...(styles?.clearIndicator?.(provided, props) ?? provided),
      padding: '0 6px',
    }),

    dropdownIndicator: (provided, props) => ({
      ...(styles?.dropdownIndicator?.(provided, props) ?? provided),
      padding: '0 6px',
    }),

    // Hide the VR
    indicatorSeparator: (provided, props) =>
      props.selectProps.isClearable && props.selectProps.value
        ? { ...provided, marginTop: '6px', marginBottom: '6px' }
        : {
            ...provided,
            display: 'none',
          },

    option: (provided, props) => ({
      ...(styles?.option?.(provided, props) ?? provided),
      // padding: '6px 10px',
      color: props.isSelected ? 'white' : 'inherit',
      backgroundColor: props.isSelected
        ? theme.colors.primary
        : props.isFocused
          ? theme.colors.fieldHover
          : 'transparent',
      ':active': {
        backgroundColor:
          !props.isDisabled &&
          (props.isSelected ? theme.colors.primary : theme.colors.fieldBorder),
      },
    }),

    menu: (base, props) => ({
      ...(styles?.menu?.(base, props) ?? base),
      zIndex: 999,
    }),

    menuList: (base, props) => ({
      ...(styles?.menuList?.(base, props) ?? base),
      whiteSpace: 'nowrap',
      overflowX: 'hidden',
    }),

    menuPortal: (base, props) => ({
      ...(styles?.menuPortal?.(base, props) ?? base),
      zIndex: 9999,
    }),
  };
};

export default generateCustomReactSelectStyles;
